import { LoginActions } from '../Services/_actions/LoginAction/LoginAction.ts';
import { resolveAuthzV2Enabled } from '../config/authz';

function stableSerialize(value) {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`;
  }

  if (value && typeof value === 'object') {
    const entries = Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`);
    return `{${entries.join(',')}}`;
  }

  return JSON.stringify(value);
}

/**
 * Compares API-backed session snapshots without depending on object identity
 * or property order.
 */
export function areUserSessionsEqual(currentUser, freshUser) {
  if (currentUser === freshUser) {
    return true;
  }
  if (!currentUser || !freshUser) {
    return false;
  }
  return stableSerialize(currentUser) === stableSerialize(freshUser);
}

/**
 * @param {object|null|undefined} userData
 */
export function normalizeUserProfilePermissions(userData) {
  if (!userData?.profile) {
    return userData;
  }

  const normalized = {
    ...userData,
    profile: { ...userData.profile },
  };

  const permissions = {};
  (normalized.profile.general_user_permission || []).forEach((perm) => {
    if (perm?.name) {
      permissions[perm.name.toLowerCase()] = perm.is_allowed;
    }
  });

  if (resolveAuthzV2Enabled(normalized)) {
    const features = normalized.profile.rbac_grants?.features || {};
    Object.entries(features).forEach(([key, value]) => {
      permissions[key.toLowerCase()] = value;
    });
  }

  normalized.profile.permissions = permissions;

  return normalized;
}

/**
 * @param {object|null|undefined} auth
 * @param {object|null|undefined} userInfo
 */
export function getSessionUserId(auth, userInfo) {
  return userInfo?.id ?? auth?.user?.id ?? auth?.user_id ?? null;
}

/**
 * Fetch latest user profile (including rbac_grants) from the API.
 *
 * @param {number|string} userId
 */
export async function fetchFreshUserSession(userId) {
  if (!userId) {
    return null;
  }

  const loginActions = LoginActions();
  const response = await loginActions.userInformation(userId);

  if (response?.status === 200 && response?.data) {
    return normalizeUserProfilePermissions(response.data);
  }

  return null;
}

/**
 * @param {object} userData
 */
export function persistUserSession(userData) {
  localStorage.setItem('userInfo', JSON.stringify(userData));
}
